import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { decryptUserKeys } from '../utils/crypto';
import config from '@/config';
import { buildPath } from '@/utils/routeBuilder';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children, apiUrl = config.AUTH_API_ADDRESS }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userProjects, setUserProjects] = useState([]);
  const [decryptedKeys, setDecryptedKeys] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  const refreshTimerRef = useRef(null);
  const isRefreshingRef = useRef(false); // Prevent concurrent refreshes
  const refreshPromiseRef = useRef(null); // Store the refresh promise for reuse
  const verifyPromiseRef = useRef(null); // Prevent concurrent verify calls

  const getCurrentProjectId = useCallback(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      if (pathParts[0] === 'projects' && pathParts.length > 1) {
        return pathParts[1];
      }
      return pathParts[0];
    }
    return null;
  }, [location.pathname]);

  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Failed to parse JWT:', e);
      return null;
    }
  };

  const scheduleTokenRefresh = useCallback((token) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const payload = parseJwt(token);
    if (!payload || !payload.exp) {
      console.warn('Cannot schedule refresh: invalid token');
      return;
    }

    // IMPORTANT: Date.now() returns local time in milliseconds
    // We need to convert to UTC seconds to match the backend's exp timestamp
    const now = Math.floor(Date.now() / 1000);
    const expiresIn = payload.exp - now;
    
    // Refresh 2 minutes before expiry (or at 50% of token lifetime if less than 4 minutes)
    const refreshIn = Math.max(expiresIn - 120, expiresIn * 0.5);
    
    console.log(`🔄 Token expires in ${expiresIn}s, will refresh in ${Math.floor(refreshIn)}s`);

    if (refreshIn > 0) {
      refreshTimerRef.current = setTimeout(async () => {
        console.log('⏰ Auto-refreshing token...');
        try {
          await refreshToken();
        } catch (error) {
          console.error('Failed to auto-refresh token:', error);
          // Don't logout immediately on first failure - could be network issue
          console.warn('Token refresh failed, will retry on next request');
        }
      }, refreshIn * 1000);
    } else {
      console.warn('Token already expired or expiring very soon');
      refreshToken().catch((error) => {
        console.error('Immediate refresh failed:', error);
      });
    }
  }, []);

  // Improved refresh token function with retry logic
  const refreshToken = async (retryCount = 0) => {
    const maxRetries = 2;
    
    // If already refreshing, return the existing promise
    if (isRefreshingRef.current && refreshPromiseRef.current) {
      console.log('Refresh already in progress, reusing promise...');
      return refreshPromiseRef.current;
    }

    isRefreshingRef.current = true;

    // Create and store the refresh promise
    refreshPromiseRef.current = (async () => {
      try {
        const refresh = sessionStorage.getItem('refresh_token');
        if (!refresh) {
          throw new Error('No refresh token available');
        }

        console.log('🔄 Refreshing token...');
        const response = await fetch(`${apiUrl}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh_token: refresh }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Token refresh failed');
        }

        const data = await response.json();
        
        // Update tokens in storage
        sessionStorage.setItem('access_token', data.access_token);
        
        if (data.refresh_token) {
          sessionStorage.setItem('refresh_token', data.refresh_token);
        }

        // Schedule next refresh
        scheduleTokenRefresh(data.access_token);
        
        console.log('✅ Token refreshed successfully');
        return data.access_token;

      } catch (error) {
        console.error('Token refresh failed:', error);
        
        // Retry logic for network errors
        if (retryCount < maxRetries && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
          console.log(`Retrying refresh (${retryCount + 1}/${maxRetries})...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
          
          // Reset refs for retry
          isRefreshingRef.current = false;
      refreshPromiseRef.current = null;
          refreshPromiseRef.current = null;
          
          return refreshToken(retryCount + 1);
        }
        
        // Only logout if refresh token is actually invalid (not just a network error)
        if (error.message.includes('Invalid') || error.message.includes('expired')) {
          await logout();
        }
        
        throw error;
      } finally {
        isRefreshingRef.current = false;
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  };

  useEffect(() => {
    checkAuthStatus();
    
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const checkAuthStatus = async () => {
    console.log('🔍 Starting auth check...');
    
    // Prevent concurrent auth checks
    if (verifyPromiseRef.current) {
      console.log('Auth check already in progress, skipping...');
      return verifyPromiseRef.current;
    }
    
    setLoading(true);
    
    verifyPromiseRef.current = (async () => {
      try {
        const token = sessionStorage.getItem('access_token');
        const storedUser = sessionStorage.getItem('user');
        
        if (token && storedUser) {
          const userData = JSON.parse(storedUser);
          console.log('✅ Setting user data:', userData);
          
          setUser(userData);
          setUserProjects(userData.projects || []);
          setIsAuthenticated(true);
          
          // Check if token is expired
          const payload = parseJwt(token);
          const now = Math.floor(Date.now() / 1000);
          
          if (payload && payload.exp && payload.exp < now) {
            console.log('Token expired, attempting refresh...');
            try {
              await refreshToken();
            } catch (error) {
              console.error('Token refresh failed during auth check:', error);
              await logout();
              return;
            }
          } else {
            // Schedule token refresh
            scheduleTokenRefresh(token);
          }
          
          // Verify token is still valid with backend
          try {
            const response = await fetch(`${apiUrl}/auth/verify`, {
              headers: {
                'Authorization': `Bearer ${sessionStorage.getItem('access_token')}`
              }
            });

            if (response.ok) {
              const data = await response.json();
              const apiProjects = data.projects || data.user?.projects || [];
              const hasValidProjects = apiProjects.length > 0 && 
                                       apiProjects[0].user_role !== undefined;
              
              const freshUserData = {
                ...userData,
                ...data.user,
                projects: hasValidProjects ? apiProjects : userData.projects
              };
              
              setUser(freshUserData);
              setUserProjects(freshUserData.projects);
              sessionStorage.setItem('user', JSON.stringify(freshUserData));
            } else if (response.status === 401) {
              console.log('Token verification failed, attempting refresh...');
              try {
                await refreshToken();
              } catch (error) {
                console.error('Refresh after verification failure failed:', error);
                await logout();
              }
            }
          } catch (verifyError) {
            console.error('Token verification failed:', verifyError);
            // Don't logout on network errors during verification
          }
        }
      } catch (error) {
        console.error('❌ Auth check failed:', error);
      } finally {
        setLoading(false);
        verifyPromiseRef.current = null;
      }
    })();
    
    return verifyPromiseRef.current;
  };

  const fetchUserInfo = async () => {
    try {
      const token = sessionStorage.getItem('access_token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await authenticatedFetch(`${apiUrl}/auth/me`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch user information');
      }

      const data = await response.json();
      
      const updatedUser = {
        id: data.id,
        email: data.email,
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        public_key: data.public_key,
        roles: data.roles || [],
        projects: data.projects || [],
        ...data
      };
      
      setUser(updatedUser);
      setUserProjects(data.projects || []);
      sessionStorage.setItem('user', JSON.stringify(updatedUser));
      
      return { success: true, user: updatedUser };
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      return { success: false, error: error.message };
    }
  };

  const login = async (email, password, projectId) => {
    try {
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          project_id: projectId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Login failed');
      }

      const data = await response.json();

      const decryptResult = await decryptUserKeys(
        password,
        data.user.encryption_data
      );

      if (!decryptResult.success) {
        throw new Error(decryptResult.error);
      }

      sessionStorage.setItem('access_token', data.access_token);
      sessionStorage.setItem('refresh_token', data.refresh_token);
      
      const userData = {
        id: data.user.id,
        email: data.user.email,
        public_key: data.user.public_key,
        roles: data.user.roles || [],
        projects: data.user.projects || []
      };
      
      sessionStorage.setItem('user', JSON.stringify(userData));

      setUser(userData);
      setUserProjects(data.user.projects || []);
      setDecryptedKeys(decryptResult);
      setIsAuthenticated(true);

      scheduleTokenRefresh(data.access_token);

      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const register = async (registrationData) => {
    try {
      const response = await fetch(`${apiUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Registration failed');
      }

      const data = await response.json();
      
      if (data.access_token) {
        sessionStorage.setItem('access_token', data.access_token);
        sessionStorage.setItem('refresh_token', data.refresh_token);
        
        const userData = {
          id: data.user.id,
          email: data.user.email,
          public_key: data.user.public_key,
          roles: data.user.roles || [],
          projects: data.user.projects || []
        };
        
        sessionStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        setUserProjects(data.user.projects || []);
        setIsAuthenticated(true);

        scheduleTokenRefresh(data.access_token);
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async (projectId = null) => {
    try {
      const token = sessionStorage.getItem('access_token');
      
      if (token) {
        await fetch(`${apiUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      isRefreshingRef.current = false;
      
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('symmetric_key');
      sessionStorage.removeItem('private_key');
      setUser(null);
      setUserProjects([]);
      setDecryptedKeys(null);
      setIsAuthenticated(false);
      
      const redirectProjectId = projectId || getCurrentProjectId();
      const baseLoginPath = redirectProjectId 
        ? `/${redirectProjectId}/login`
        : '/login';
      
      const redirectPath = buildPath(baseLoginPath);
      navigate(redirectPath, { replace: true });
    }
  };

  const hasProjectAccess = useCallback((projectId) => {
    if (!user || !userProjects.length) return false;
    return userProjects.some(project => 
      project.id === projectId || project.slug === projectId
    );
  }, [user, userProjects]);

  const hasRole = useCallback((role) => {
    if (!user) return false;
    return user.roles && user.roles.includes(role);
  }, [user]);

  const hasProjectRole = useCallback((projectId, role) => {
    if (!user || !userProjects.length) return false;
    
    const project = userProjects.find(p => 
      p.id === projectId || p.slug === projectId
    );
    
    if (!project) return false;
    
    const userRole = project.user_role;
    
    // Direct role match - no hierarchy
    return userRole === role;
  }, [userProjects, user]);       

  const authenticatedFetch = async (url, options = {}) => {
    let token = sessionStorage.getItem('access_token');
    
    const makeRequest = async (accessToken) => {
      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${accessToken}`,
        },
      });
    };

    let response = await makeRequest(token);

    // If 401, try to refresh token once
    if (response.status === 401) {
      try {
        console.log('Request failed with 401, refreshing token...');
        const newToken = await refreshToken();
        response = await makeRequest(newToken);
        
        // If still 401 after refresh, logout
        if (response.status === 401) {
          console.error('Still unauthorized after refresh, logging out');
          await logout();
          throw new Error('Session expired');
        }
      } catch (error) {
        console.error('Failed to refresh token:', error);
        await logout();
        throw new Error('Session expired');
      }
    }

    return response;
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    userProjects,
    decryptedKeys,
    login,
    register,
    logout,
    refreshToken,
    fetchUserInfo,
    hasProjectAccess,
    hasRole,
    hasProjectRole,
    authenticatedFetch,
    getCurrentProjectId,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};